import React, { useState, useEffect } from 'react';
import { Calendar, Clock, ArrowLeft, Share2, Facebook, Twitter, Linkedin, Eye, ThumbsUp, MessageCircle, ArrowRight, Loader2, AlertCircle } from 'lucide-react';

// Props interface for the component
const NewsDetail = ({ slug, onBack }) => {
  const [isDarkMode] = useState(false); // Replace with your actual dark mode context
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [likes, setLikes] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [comments, setComments] = useState(0);

  // API Base URL - sesuaikan dengan backend Anda
  const API_BASE_URL = (import.meta.env.VITE_API_URL || 'http://localhost:5000') + '/api';

  // Fungsi untuk mendapatkan nilai yang aman untuk di-render
  const getSafeValue = (value, fallback = '') => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return value.toString();
    if (typeof value === 'object') {
      if (value.name) return value.name;
      if (value.title) return value.title;
      return JSON.stringify(value);
    }
    return fallback;
  };

  const getCategoryName = (category) => {
    return getSafeValue(category, 'General');
  };

  const getAuthorName = (author) => {
    return getSafeValue(author, 'Admin Telnet Home');
  };

  // Fetch article by slug
  const fetchArticle = async (articleSlug) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/news/slug/${articleSlug}`);
      const result = await response.json();
      
      if (result.success && result.data) {
        const safeArticle = {
          ...result.data,
          title: getSafeValue(result.data.title, 'Untitled'),
          content: getSafeValue(result.data.content, ''),
          summary: getSafeValue(result.data.summary, ''),
          category: getCategoryName(result.data.category),
          author: getAuthorName(result.data.author)
        };
        setArticle(safeArticle);
        setLikes(safeArticle.likes || 0);
        setComments(safeArticle.comments || 0);
        
        // Update view count
        updateViewCount(articleSlug);
        
        // Fetch related articles
        fetchRelatedArticles(result.data.category, articleSlug);
      } else {
        setError('Artikel tidak ditemukan');
      }
    } catch (error) {
      console.error('Error fetching article:', error);
      setError('Gagal memuat artikel');
    } finally {
      setLoading(false);
    }
  };

  // Update view count
  const updateViewCount = async (articleSlug) => {
    try {
      await fetch(`${API_BASE_URL}/news/slug/${articleSlug}/view`, {
        method: 'POST'
      });
    } catch (error) {
      console.error('Error updating view count:', error);
    }
  };

  // Fetch related articles
  const fetchRelatedArticles = async (category, currentSlug) => {
    try {
      const categoryParam = typeof category === 'object' ? category.name || category.title : category;
      const response = await fetch(
        `${API_BASE_URL}/news?category=${categoryParam}&limit=3&exclude=${currentSlug}`
      );
      const result = await response.json();
      
      if (result.success && result.data) {
        const safeRelatedArticles = result.data.news.map(relatedArticle => ({
          ...relatedArticle,
          title: getSafeValue(relatedArticle.title, 'Untitled'),
          category: getCategoryName(relatedArticle.category)
        }));
        setRelatedArticles(safeRelatedArticles);
      }
    } catch (error) {
      console.error('Error fetching related articles:', error);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    try {
      const options = { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric'
      };
      return new Date(dateString).toLocaleDateString('id-ID', options);
    } catch (error) {
      return 'Tanggal tidak valid';
    }
  };

  // Handle like
  const handleLike = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/news/slug/${slug}/like`, {
        method: 'POST'
      });
      const result = await response.json();
      
      if (result.success) {
        setLikes(result.data.likes);
        setIsLiked(!isLiked);
      }
    } catch (error) {
      console.error('Error liking article:', error);
    }
  };

  // Handle share
  const handleShare = (platform) => {
    const url = window.location.href;
    const text = article?.title || '';
    
    switch(platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`);
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`);
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`);
        break;
      default:
        if (navigator.clipboard) {
          navigator.clipboard.writeText(url);
          alert('Link berhasil disalin ke clipboard!');
        }
    }
  };

  // Handle related article click
  const handleRelatedArticleClick = (articleSlug) => {
    // Update URL and fetch new article
    window.history.pushState({}, '', `/news/${articleSlug}`);
    fetchArticle(articleSlug);
  };

  // Initial fetch when component mounts or slug changes
  useEffect(() => {
    if (slug) {
      fetchArticle(slug);
    }
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-[#1E9B4C]" />
          <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Memuat artikel...
          </p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 mx-auto mb-4 text-red-500" />
          <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {error || 'Artikel tidak ditemukan'}
          </p>
          <button 
            onClick={() => onBack ? onBack() : window.history.back()}
            className="bg-[#1E9B4C] hover:bg-green-700 text-white px-4 py-2 rounded-lg"
          >
            Kembali ke Berita
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen py-8 px-4 bg-cover bg-center transition-colors duration-300"
      style={{
        backgroundImage: `url('https://telnet.net.id/assets/bg.png')`,
        backgroundColor: isDarkMode ? '#111827' : '#dfe4e0ff',
        backgroundBlendMode: 'overlay'
      }}
    >
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button 
          onClick={() => onBack ? onBack() : window.history.back()}
          className={`inline-flex items-center gap-2 mb-8 px-4 py-2 rounded-lg transition-colors ${
            isDarkMode 
              ? 'text-gray-300 hover:text-white hover:bg-gray-800' 
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
          }`}
        >
          <ArrowLeft className="w-4 h-4" />
          Kembali ke Berita
        </button>

        {/* Article Header */}
        <div className={`rounded-2xl overflow-hidden shadow-lg mb-8 ${
          isDarkMode ? 'bg-gray-800' : 'bg-white'
        }`}>
          {/* Hero Image */}
          {article.image && (
            <div className="relative">
              <img 
                src={getSafeValue(article.image, 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1200&h=600&fit=crop')} 
                alt={getSafeValue(article.title)}
                className="w-full h-64 md:h-96 object-cover"
                onError={(e) => {
                  e.target.src = 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1200&h=600&fit=crop';
                }}
              />
              <div className="absolute top-4 left-4">
                <span className="bg-[#1E9B4C] text-white px-3 py-1 rounded-full text-sm font-medium">
                  {getCategoryName(article.category)}
                </span>
              </div>
            </div>
          )}

          {/* Article Meta */}
          <div className="p-6 md:p-8">
            <h1 className={`text-3xl md:text-4xl font-bold mb-4 leading-tight ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              {getSafeValue(article.title)}
            </h1>
            
            {article.summary && (
              <p className={`text-lg md:text-xl mb-6 leading-relaxed ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {getSafeValue(article.summary)}
              </p>
            )}

            {/* Author & Meta Info */}
            <div className="flex flex-wrap items-center gap-4 mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <img 
                  src={article.author?.avatar || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=60&h=60&fit=crop&crop=face'} 
                  alt={getAuthorName(article.author)}
                  className="w-10 h-10 rounded-full"
                />
                <div>
                  <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {getAuthorName(article.author)}
                  </div>
                </div>
              </div>
              
              <div className={`flex items-center gap-4 text-sm ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(article.publishedAt || article.createdAt)}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>{Math.ceil((article.content?.length || 0) / 1000)} min read</span>
                </div>
                {article.views !== undefined && (
                  <div className="flex items-center gap-1">
                    <Eye className="w-4 h-4" />
                    <span>{article.views.toLocaleString()} views</span>
                  </div>
                )}
              </div>
            </div>

            {/* Social Actions */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-4">
                <button
                  onClick={handleLike}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    isLiked 
                      ? 'bg-[#1E9B4C] text-white' 
                      : isDarkMode 
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <ThumbsUp className="w-4 h-4" />
                  <span>{likes}</span>
                </button>
                
                <button className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  isDarkMode 
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}>
                  <MessageCircle className="w-4 h-4" />
                  <span>{comments}</span>
                </button>
              </div>

              <div className="flex items-center gap-2">
                <span className={`text-sm mr-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Share:
                </span>
                <button 
                  onClick={() => handleShare('facebook')}
                  className={`p-2 rounded-lg transition-colors ${
                    isDarkMode 
                      ? 'text-gray-400 hover:text-blue-400 hover:bg-gray-700' 
                      : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'
                  }`}
                >
                  <Facebook className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => handleShare('twitter')}
                  className={`p-2 rounded-lg transition-colors ${
                    isDarkMode 
                      ? 'text-gray-400 hover:text-blue-400 hover:bg-gray-700' 
                      : 'text-gray-500 hover:text-blue-400 hover:bg-blue-50'
                  }`}
                >
                  <Twitter className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => handleShare('linkedin')}
                  className={`p-2 rounded-lg transition-colors ${
                    isDarkMode 
                      ? 'text-gray-400 hover:text-blue-400 hover:bg-gray-700' 
                      : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'
                  }`}
                >
                  <Linkedin className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => handleShare('copy')}
                  className={`p-2 rounded-lg transition-colors ${
                    isDarkMode 
                      ? 'text-gray-400 hover:text-green-400 hover:bg-gray-700' 
                      : 'text-gray-500 hover:text-green-600 hover:bg-green-50'
                  }`}
                >
                  <Share2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div 
              className={`prose prose-lg max-w-none ${
                isDarkMode 
                  ? 'prose-invert prose-headings:text-white prose-p:text-gray-300 prose-strong:text-white prose-li:text-gray-300' 
                  : 'prose-gray'
              }`}
              dangerouslySetInnerHTML={{ __html: getSafeValue(article.content, '<p>Konten tidak tersedia</p>') }}
            />
          </div>
        </div>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <div className={`rounded-2xl p-6 md:p-8 shadow-lg ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <h3 className={`text-2xl font-bold mb-6 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Artikel Terkait
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedArticles.map((relatedArticle) => (
                <div 
                  key={relatedArticle._id} 
                  className={`rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer group ${
                    isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
                  }`}
                  onClick={() => handleRelatedArticleClick(relatedArticle.slug)}
                >
                  <img 
                    src={getSafeValue(relatedArticle.image, 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=300&h=200&fit=crop')} 
                    alt={getSafeValue(relatedArticle.title)}
                    className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=300&h=200&fit=crop';
                    }}
                  />
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="bg-green-100 dark:bg-green-900/30 text-[#1E9B4C] px-2 py-1 rounded text-xs font-medium">
                        {getCategoryName(relatedArticle.category)}
                      </span>
                      <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {formatDate(relatedArticle.publishedAt || relatedArticle.createdAt)}
                      </span>
                    </div>
                    <h4 className={`font-medium group-hover:text-[#1E9B4C] transition-colors line-clamp-2 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {getSafeValue(relatedArticle.title)}
                    </h4>
                    <div className="mt-3">
                      <span className="inline-flex items-center gap-1 text-[#1E9B4C] hover:text-green-700 text-sm font-medium">
                        Baca selengkapnya
                        <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsDetail;